import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import {
  Button,
  Box,
  useMediaQuery,
  useTheme,
  Switch,
  Typography,
} from "@mui/material";
import AddIcon from "@icons/myicons/plus.svg?react";
import LockIcon from "@icons/myicons/lock.svg?react";
import {
  AccountsContainer,
  CardPageLayout,
  LoadingPlaceHolder,
  SearchField,
  NoDataStub,
} from "@components";
import { AccountTypeEnum } from "@api";
import { ImportType } from "@features/add/components/types";
import { AddPortfolio, AddPortfolioHandle } from "@features/add/AddPortfolio";
import { PortfolioLimitGate } from "@features/add/PortfolioLimitGate";
import { Chips, FilterItem, FilterValue } from "@components/filters";
import { accountTypeIconMapper } from "@icons/utils";
import { useAccountsByGroup } from "./useAccountsByGroup";
import { useScrollToHashedRow } from "./useScrollToHashedRow";

export const groupTitleMapper: Partial<Record<AccountTypeEnum, string>> = {
  "01_depot": "depotPage.subcategories.depots",
  "02_cash": "depotPage.subcategories.cash",
  "05_material_assets": "depotPage.subcategories.assets",
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const ActionButton = ({
  filter,
  setFilter,
  handleOpenMenuClick,
}: {
  filter: string;
  setFilter: (e: string) => void;
  handleOpenMenuClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection={isMobile ? "column-reverse" : "row"}
      gap={3}
      width={isMobile ? "100%" : "auto"}
    >
      <SearchField
        value={filter}
        data-testid="depots-filter-field"
        onChange={(e) => setFilter(e.target.value)}
        sx={{
          "& input": {
            height: "28px",
          },
        }}
      />
      <PortfolioLimitGate
        renderLocked={
          <Button
            variant="tertiary"
            startIcon={<LockIcon width="16px" height="16px" />}
            fullWidth={isMobile}
            sx={{
              cursor: "not-allowed",
              padding: "10px 16px 10px 20px",
              minWidth: "144px",
            }}
          >
            {t("accounts.addButton")}
          </Button>
        }
      >
        <Button
          variant="primary"
          startIcon={<AddIcon width="16px" height="16px" />}
          data-testid="add-account"
          fullWidth={isMobile}
          sx={{
            padding: "10px 16px 10px 20px",
            minWidth: "144px",
          }}
          onClick={handleOpenMenuClick}
        >
          {t("accounts.addButton")}
        </Button>
      </PortfolioLimitGate>
    </Box>
  );
};

export const DepotsAccountsPage = () => {
  const { t } = useTranslation();
  const addPortfolioRef = useRef<AddPortfolioHandle | null>(null);
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showHidden, setShowHidden] = useState(false);
  const [selectedChips, setSelectedChips] = useState<FilterValue[]>(["all"]);
  const [filter, setFilter] = useState("");

  const {
    accounts,
    groupedAccounts,
    refMapByDepotId,
    isLoading: isAccountsLoading,
    hiddenAccountsCount,
    groups,
  } = useAccountsByGroup({ showHidden });
  const groupsToShow = selectedChips.includes("all") ? groups : selectedChips;

  useScrollToHashedRow(!isAccountsLoading);

  const categoryOptions = useMemo(
    () =>
      ["all", ...groups].map<FilterItem>((i) => ({
        text: t(`depotsAccountsPage.chips.${i}`),
        value: i,
        clickable: true,
        icon: accountTypeIconMapper[i as AccountTypeEnum],
      })),
    [groups, t]
  );

  const onSelect = useCallback((val: ImportType) => {
    addPortfolioRef.current?.onSelect(val);
  }, []);

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuEl(event.currentTarget);
  };

  const [hasAutoOpenedModal, setHasAutoOpenedModal] = useState(false);
  if (!hasAutoOpenedModal && !isAccountsLoading && accounts?.length === 0) {
    setHasAutoOpenedModal(true);
    setIsModalOpen(true);
  }

  return (
    <CardPageLayout
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        { text: t("breadcrumbs.depotsAccounts") },
      ]}
      cardHeading={
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={5}
        >
          <Typography variant="h4">{t("depots.title")}</Typography>
          <ActionButton
            filter={filter}
            setFilter={setFilter}
            handleOpenMenuClick={handleOpenMenuClick}
          />
        </Box>
      }
      cardChildren={
        <>
          {isAccountsLoading ? (
            <Box width="100%" mt={6}>
              <LoadingPlaceHolder height="300px" />
            </Box>
          ) : Object.keys(groupedAccounts)?.length === 0 ? (
            <Box mt={10} mb={6}>
              <NoDataStub />
            </Box>
          ) : (
            <>
              <Box my={6}>
                <Chips
                  options={categoryOptions}
                  selectedOptions={selectedChips}
                  setSelectedOptions={setSelectedChips}
                />
              </Box>
              {hiddenAccountsCount > 0 && (
                <Wrapper>
                  <Switch
                    checked={showHidden}
                    color="primary"
                    data-testid="hidden-switch"
                    onChange={() => setShowHidden(!showHidden)}
                    sx={{
                      marginRight: 3,
                    }}
                    size="medium"
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {t("depotsAccountsPage.showHiddenItems")}:{" "}
                    {hiddenAccountsCount}
                  </Typography>
                </Wrapper>
              )}
              <Box display="flex" flexDirection="column" gap={6} mt={6}>
                {groupedAccounts.map(({ title, accounts }, index) => {
                  if (!groupsToShow.includes(title)) return null;
                  const importType =
                    title === "01_depot" || title === "02_cash"
                      ? ""
                      : "manually";

                  return (
                    <AccountsContainer
                      key={title}
                      type={title}
                      title={t(groupTitleMapper[title] ?? "")}
                      accounts={accounts}
                      refAccountsByDepotId={
                        title === "01_depot" ? refMapByDepotId : {}
                      }
                      onAdd={() =>
                        onSelect({ type: importType, default: title })
                      }
                      isFetching={isAccountsLoading}
                      filter={filter}
                      isLastGroup={index === groupedAccounts.length - 1}
                    />
                  );
                })}
              </Box>
            </>
          )}
          <AddPortfolio
            ref={addPortfolioRef}
            menuEl={menuEl}
            setMenuEl={setMenuEl}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
          />
        </>
      }
    >
      <></>
    </CardPageLayout>
  );
};
